import { type ButtonHandle, createButton } from '../primitives/button';
import { type ModalHandle, createModal } from '../primitives/modal';
import './game-over-screen.css';

export interface GameOverScreenOptions {
  score: number;
  previousBest?: number;
  onReplay: () => void;
  onShare?: () => void;
  onMenu?: () => void;
  onSettings?: () => void;
  scoreLabel?: string;
}

export interface GameOverScreenHandle {
  readonly el: HTMLElement;
  show(): void;
  hide(): void;
  destroy(): void;
}

const fmt = (n: number) => n.toLocaleString();

export function createGameOverScreen(opts: GameOverScreenOptions): GameOverScreenHandle {
  const body = document.createElement('div');
  body.className = 'fg-gameover__body';

  if (opts.previousBest !== undefined && opts.score > opts.previousBest) {
    const badge = document.createElement('span');
    badge.className = 'fg-gameover__badge';
    badge.textContent = '★ NEW BEST';
    body.appendChild(badge);
  }

  const label = document.createElement('div');
  label.className = 'fg-gameover__score-label';
  label.textContent = opts.scoreLabel ?? 'Final score';
  body.appendChild(label);

  const score = document.createElement('div');
  score.className = 'fg-gameover__score';
  score.textContent = fmt(opts.score);
  body.appendChild(score);

  if (opts.previousBest !== undefined) {
    const prev = document.createElement('div');
    prev.className = 'fg-gameover__prev-best';
    prev.textContent = `Previous best ${fmt(opts.previousBest)}`;
    body.appendChild(prev);
  }

  const iconButtons: ButtonHandle[] = [];
  function addIcon(icon: string, ariaLabel: string, cb: () => void) {
    iconButtons.push(createButton({ variant: 'icon', icon, ariaLabel, onClick: cb }));
  }
  if (opts.onShare) addIcon('↗', 'Share', opts.onShare);
  if (opts.onMenu) addIcon('☰', 'Menu', opts.onMenu);
  if (opts.onSettings) addIcon('⚙', 'Settings', opts.onSettings);

  if (iconButtons.length > 0) {
    const iconRow = document.createElement('div');
    iconRow.className = 'fg-gameover__icon-row';
    for (const ib of iconButtons) iconRow.appendChild(ib.el);
    body.appendChild(iconRow);
  }

  const replayBtn = createButton({
    variant: 'primary',
    label: 'PLAY AGAIN',
    onClick: () => {
      modal.hide();
      opts.onReplay();
    },
  });

  const modal: ModalHandle = createModal({
    body,
    actions: [replayBtn],
    dismissible: false,
  });

  return {
    el: modal.el,
    show: () => modal.show(),
    hide: () => modal.hide(),
    destroy() {
      for (const ib of iconButtons) ib.destroy();
      replayBtn.destroy();
      modal.destroy();
    },
  };
}
